import { useEffect } from "react";
import { usePatchPortfolioLifeInsuranceMutation } from "web_myplann/Api/Portfolio/Mutations/usePatchPortfolioLifeInsuranceMutation";
import type { PortfolioDetailResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { getPortfolioTitle } from "web_myplann/Portfolio/Components/PortfolioSections/portfolioListItemHelpers";
import {
	getLifeInsuranceFormSchema,
	type LifeInsuranceFormOutput,
} from "web_myplann/Portfolio/Forms/LifeInsuranceForm/lifeInsuranceFormSchema";
import { mapLifeInsuranceEditFormToRequest } from "web_myplann/Portfolio/Forms/LifeInsuranceForm/mapLifeInsuranceFormToRequest";
import { mapPortfolioToLifeInsuranceForm } from "web_myplann/Portfolio/Forms/LifeInsuranceForm/mapPortfolioToLifeInsuranceForm";
import { useEditPortfolioForm } from "web_myplann/Portfolio/PortfolioShared/useEditPortfolioForm";

type UseEditLifeInsuranceFormArgs = {
	portfolioId: number;
	portfolio: PortfolioDetailResponseDTO;
	detailRoute: string;
};

export const useEditLifeInsuranceForm = ({
	portfolioId,
	portfolio,
	detailRoute,
}: UseEditLifeInsuranceFormArgs) => {
	const { t } = useResource();
	const { mutate, isPending } =
		usePatchPortfolioLifeInsuranceMutation(portfolioId);

	const { form, onSubmit, leaveConfirmation } = useEditPortfolioForm({
		schema: getLifeInsuranceFormSchema(t),
		defaultValues: mapPortfolioToLifeInsuranceForm(portfolio, t),
		mutate: (data: Partial<LifeInsuranceFormOutput>, options) => {
			mutate(
				mapLifeInsuranceEditFormToRequest(
					data,
					{
						insurance: form.getValues("insurance") ?? null,
						paymentFrequency: form.getValues("paymentFrequency"),
					},
					portfolio.tools,
				),
				options,
			);
		},
		successToast: t(Resources.Portfolio.EditPortfolio.savedToast, {
			type: getPortfolioTitle(portfolio, t),
		}),
		redirectTo: detailRoute,
		isPending,
	});

	useEffect(() => {
		form.trigger();
	}, [form.trigger]);

	return { form, onSubmit, isPending, leaveConfirmation };
};
